import React from 'react';
import MetricCard, { MetricCardGroup } from './MetricCard';
import Panel from './Panel';
import StackedTrendChart from './charts/StackedTrendChart';
import SimpleTrendChart from './charts/SimpleTrendChart';
import useDefectSeverityIndex from '../../hooks/useDefectSeverityIndex';
import useDefectSeverityIndexBreakdown from '../../hooks/useDefectSeverityIndexBreakdown';
import useSupportBugsByTeamTime from '../../hooks/useSupportBugsByTeamTime';
import useProductionIncidentsByTeamTime from '../../hooks/useProductionIncidentsByTeamTime';
import useEpdBugsByTeamTime from '../../hooks/useEpdBugsByTeamTime';
import { DSI_TIER_SERIES, DSI_LINE, PI_PRIORITY_SERIES, SUP_PRIORITY_SERIES, EPD_SEVERITY_SERIES } from './chartColors';

const EMPTY_MESSAGE = 'No data for the current filters.';

const EmptyState = () => <div className="qk-table-empty" role="status">{EMPTY_MESSAGE}</div>;

// The API nests tier counts under byPriority; StackedTrendChart reads them from the row's top level.
const flattenPriority = rows => rows.map(({ byPriority, ...rest }) => ({ ...rest, ...byPriority }));
const flattenSeverity = rows => rows.map(({ bySeverity, ...rest }) => ({ ...rest, ...bySeverity }));

// The API returns an unrounded dsi; round to 2 decimals as a number (trailing zeros drop, e.g. 75.4).
// Missing values pass through unchanged so a malformed row can't crash the render.
const roundDsi = dsi => (dsi == null ? dsi : Number(dsi.toFixed(2)));

// The DSI breakdown nests tier counts under tierCounts.
const flattenDsiRows = rows => rows.map(({ tierCounts, dsi, ...rest }) => ({
  ...rest,
  ...tierCounts,
  dsi: roundDsi(dsi),
}));

// Live chart body: error, loading and empty states first, never mock data.
function LiveChart({ result: { data: rows, loading, error }, label, prepare = r => r, children }) {
  if (error) return <div className="section-error">Couldn’t load {label} data.</div>;
  if (loading) return <div className="section-loading">Loading…</div>;
  if (!rows || rows.length === 0) return <EmptyState />;
  return children(prepare(rows));
}

const PiChart = props => <LiveChart label="Production Incident" {...props} />;
const SupportChart = props => <LiveChart label="Support Bugs" {...props} />;
const DsiChart = props => <LiveChart label="Defect Severity Index" prepare={flattenDsiRows} {...props} />;

// DSI summary cards: loading and error states first, never mock data.
function DsiSummary({ result: { data: dsiData, loading, error } }) {
  if (error) return <div className="section-error">Couldn’t load Defect Severity Index data.</div>;
  if (loading) return <div className="section-loading">Loading…</div>;
  if (!dsiData || !dsiData.tierCounts || !dsiData.sourceCounts) return <EmptyState />;

  const { tierCounts, sourceCounts } = dsiData;
  return (
    <>
      <MetricCardGroup>
        <MetricCard label="#Production Incident" value={sourceCounts.pi ?? 0} />
        <MetricCard label="#Bugs from Support" value={sourceCounts.sup ?? 0} />
        <MetricCard label="#Bugs from EPD" value={sourceCounts.epd ?? 0} />
        <MetricCard label="#Total Bugs which Impacted Customers" value={(sourceCounts.pi ?? 0) + (sourceCounts.sup ?? 0) + (sourceCounts.epd ?? 0)} />
      </MetricCardGroup>
      <MetricCardGroup>
        <MetricCard label="Critical Impact" value={tierCounts.critical} />
        <MetricCard label="Major Impact" value={tierCounts.major} />
        <MetricCard label="Moderate Impact" value={tierCounts.moderate} />
        <MetricCard label="Minor Impact" value={tierCounts.minor} />
        <MetricCard label="[V2] Defect Severity Index (DSI)" value={roundDsi(dsiData.dsi)} info="100 * LOG10(weightedSum + 1) / LOG10(8001) — see QA-7133" />
      </MetricCardGroup>
    </>
  );
}

function DefectSeverityIndexTab({ filters, dateRange }) {
  const dsi = useDefectSeverityIndex(filters, dateRange);
  const piByUnit = useProductionIncidentsByTeamTime('unit', filters, dateRange);
  const piByGroup = useProductionIncidentsByTeamTime('group', filters, dateRange);
  const piByMonth = useProductionIncidentsByTeamTime('month', filters, dateRange);
  const piByWeek = useProductionIncidentsByTeamTime('week', filters, dateRange);
  const dsiByUnit = useDefectSeverityIndexBreakdown('unit', filters, dateRange);
  const dsiByWeek = useDefectSeverityIndexBreakdown('week', filters, dateRange);
  const supByUnit = useSupportBugsByTeamTime('unit', filters, dateRange);
  const supByMonth = useSupportBugsByTeamTime('month', filters, dateRange);
  const supByWeek = useSupportBugsByTeamTime('week', filters, dateRange);
  const epdByUnit = useEpdBugsByTeamTime('unit', filters, dateRange);
  const epdByMonth = useEpdBugsByTeamTime('month', filters, dateRange);
  const epdByWeek = useEpdBugsByTeamTime('week', filters, dateRange);

  return (
    <div className="qk-tab-content">
      <Panel accent="green">
        <DsiSummary result={dsi} />
      </Panel>

      <Panel title="[V2] Defect Severity Index - Per Team" accent="green">
        <DsiChart result={dsiByUnit}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={DSI_TIER_SERIES} line={DSI_LINE} />}
        </DsiChart>
      </Panel>
      <Panel title="[V2] Defect Severity Index - Severity Showdown: Weekly Battle" accent="green">
        <DsiChart result={dsiByWeek}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={DSI_TIER_SERIES} line={DSI_LINE} />}
        </DsiChart>
      </Panel>

      <Panel title="Production Incident (excluding reported by PagerDuty) - Per Team" accent="pink">
        <PiChart result={piByUnit} prepare={flattenPriority}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={PI_PRIORITY_SERIES} />}
        </PiChart>
      </Panel>
      <Panel title="Production Incident (excluding reported by PagerDuty) - Per Group" accent="pink">
        <PiChart result={piByGroup} prepare={flattenPriority}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={PI_PRIORITY_SERIES} />}
        </PiChart>
      </Panel>
      <Panel accent="pink">
        <div className="qk-chart-row">
          <div className="qk-chart-col">
            <div className="qk-chart-title">Production Incident — Month over Month</div>
            <PiChart result={piByMonth}>
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </PiChart>
          </div>
          <div className="qk-chart-col">
            <div className="qk-chart-title">Production Incident — Week over Week</div>
            <PiChart result={piByWeek}>
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </PiChart>
          </div>
        </div>
      </Panel>

      <Panel title="Bugs reported by Customer/Support - Per Team" accent="amber">
        <SupportChart result={supByUnit} prepare={flattenPriority}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={SUP_PRIORITY_SERIES} />}
        </SupportChart>
      </Panel>
      <Panel accent="amber">
        <div className="qk-chart-row">
          <div className="qk-chart-col">
            <div className="qk-chart-title">Bugs reported by Customer/Support — Month over Month</div>
            <SupportChart result={supByMonth}>
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </SupportChart>
          </div>
          <div className="qk-chart-col">
            <div className="qk-chart-title">Bugs reported by Customer/Support — Week over Week</div>
            <SupportChart result={supByWeek}>
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </SupportChart>
          </div>
        </div>
      </Panel>

      <Panel title="Bugs identified by Internal Employees in Production - Per Team" accent="blue">
        <LiveChart result={epdByUnit} label="EPD Bugs" prepare={flattenSeverity}>
          {rows => <StackedTrendChart data={rows} dimensionKey="bucket" series={EPD_SEVERITY_SERIES} />}
        </LiveChart>
      </Panel>
      <Panel accent="blue">
        <div className="qk-chart-row">
          <div className="qk-chart-col">
            <div className="qk-chart-title">Month over Month</div>
            <LiveChart result={epdByMonth} label="EPD Bugs">
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </LiveChart>
          </div>
          <div className="qk-chart-col">
            <div className="qk-chart-title">Week over Week</div>
            <LiveChart result={epdByWeek} label="EPD Bugs">
              {rows => <SimpleTrendChart data={rows} dimensionKey="bucket" valueKey="total" color="#c2185b" />}
            </LiveChart>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default DefectSeverityIndexTab;
